import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn } from '../components/adminTable';
import { AuditService, AuditLog } from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { formatDateDisplay } from '../../../utils/dateHelper';
// import ExportButton from '../../sharedComponents/ExportButton';

function AuditLogs() {
    const { showNotification } = useNotification();
    const [auditData, setAuditData] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        entityName: '',
        correlationId: '',
        userName: '',
        from: null as string | null,
        to: null as string | null,
        action: '',
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchAuditLogs();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, filters]);


    const fetchAuditLogs = async () => {
        setLoading(true);
        try {
            const response = await AuditService.getAuditLogs({
                pageNumber: currentPage,
                pageSize,
                entityName: filters.entityName || '',
                correlationId: filters.correlationId || '',
                userName: filters.userName || '',
                from: filters.from,
                to: filters.to,
                action: filters.action || ''
            });

            if (response.isSuccess) {
                setAuditData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch audit logs'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching audit logs'
            });
            } finally {
            setLoading(false);
        }
    };

    // ── Filter handlers ────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, userName: query }));
        setCurrentPage(1);
    };

    const handleFilterChange = (field: string, value: string | null) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({ entityName: '', correlationId: '', userName: '', from: null, to: null, action: '' });
        setCurrentPage(1);
    };

    // ── Pagination handlers ────────────────────────────────────────────────────

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const formatDate = (dateString: string) => formatDateDisplay(dateString);

    const getActionBadgeColor = (action: string) => {
        switch (action.toUpperCase()) {
            case 'CREATED': return 'text-green-600';
            case 'MODIFIED': return 'text-blue-600';
            case 'DELETED': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    const formatJsonForDisplay = (jsonString: string) => {
        try {
            const parsed = JSON.parse(jsonString);
            const entries = Object.entries(parsed);
            if (entries.length === 0) return 'N/A';
            const preview = entries.slice(0, 2).map(([key, value]) => {
                const displayValue = typeof value === 'string' && value.length > 20
                    ? value.substring(0, 20) + '...'
                    : String(value);
                return `${key}: ${displayValue}`;
            }).join(', ');
            return entries.length > 2 ? `${preview}...` : preview;
        } catch {
            return jsonString.length > 50 ? jsonString.substring(0, 50) + '...' : jsonString;
        }
    };

    const formatJsonForTooltip = (jsonString: string) => {
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2);
        } catch {
            return jsonString;
        }
    };

    // ── Table columns ──────────────────────────────────────────────────────────

    const auditColumns: TableColumn<AuditLog>[] = [
        {
            key: 'entityName',
            label: 'Entity',
            render: (_, record) => (
                <div className="flex flex-col gap-1">
                    <span className="font-medium">{record.entityName}</span>
                    <span className="text-xs text-gray-600">{formatDate(record.performedAtUtc)}</span>
                </div>
            )
        },
        {
            key: 'performedBy',
            label: 'Performed By',
            render: (_, record) => (
                <div className="flex flex-col gap-1">
                    <span className="text-gray-700">{record.performedBy}</span>
                    <span className={`text-[0.75em] font-bold leading-[1] whitespace-nowrap ${getActionBadgeColor(record.action)}`}>
                        {record.action}
                    </span>
                </div>
            )
        },
        {
            key: 'oldValues',
            label: 'Old Values',
            render: (value) => (
                <div className="relative group max-w-[200px]">
                    <span className="text-xs text-gray-600 truncate block cursor-help">
                        {formatJsonForDisplay(value)}
                    </span>
                    <div className="invisible group-hover:visible absolute z-50 left-0 bottom-full mb-1 p-3 bg-gray-900 text-white text-xs rounded-md shadow-lg max-w-md w-max">
                        <pre className="whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                            {formatJsonForTooltip(value)}
                        </pre>
                    </div>
                </div>
            )
        },
        {
            key: 'newValues',
            label: 'New Values',
            render: (value) => (
                <div className="relative group max-w-[200px]">
                    <span className="text-xs text-gray-600 truncate block cursor-help">
                        {formatJsonForDisplay(value)}
                    </span>
                    <div className="invisible group-hover:visible absolute z-50 left-0 bottom-full mb-1 p-3 bg-gray-900 text-white text-xs rounded-md shadow-lg max-w-md w-max">
                        <pre className="whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                            {formatJsonForTooltip(value)}
                        </pre>
                    </div>
                </div>
            )
        },
        {
            key: 'correlationId',
            label: 'Correlation ID',
            render: (value) => (
                <span className="text-xs font-mono text-gray-500 block" title={value}>
                    {value}
                </span>
            )
        }
    ];

    // ── Filter panel ───────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name</label>
                    <input
                        type="text"
                        value={filters.entityName}
                        onChange={(e) => handleFilterChange('entityName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., MenuInRole"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
                    <input
                        type="text"
                        value={filters.userName}
                        onChange={(e) => handleFilterChange('userName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter username"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                        value={filters.action}
                        onChange={(e) => handleFilterChange('action', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Actions</option>
                        <option value="CREATED">Created</option>
                        <option value="MODIFIED">Modified</option>
                        <option value="DELETED">Deleted</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                        type="datetime-local"
                        value={filters.from || ''}
                        onChange={(e) => handleFilterChange('from', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                        type="datetime-local"
                        value={filters.to || ''}
                        onChange={(e) => handleFilterChange('to', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Correlation ID</label>
                    <input
                        type="text"
                        value={filters.correlationId}
                        onChange={(e) => handleFilterChange('correlationId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter correlation ID"
                    />
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    {/* Header */}
                    <div className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Audit Logs</h1>
                            <p className="text-gray-500 text-sm sm:text-base">
                                View and track system activities and user actions.
                            </p>
                        </div>
                        {/*
                        <ExportButton
                            reportType={3}
                            parameters={{
                                entityName: filters.entityName,
                                correlationId: filters.correlationId,
                                userName: filters.userName,
                                from: filters.from,
                                to: filters.to,
                                action: filters.action
                            }}
                            reportTitle="Audit Logs Report"
                            buttonText="Export Audit Logs"
                            className="w-full md:w-auto"
                        />
                        */}
                    </div>

                    {/* Table */}
                    <ReusableTable
                        title="Audit Logs"
                        columns={auditColumns}
                        data={auditData}
                        loading={loading}
                        searchPlaceholder="Search by username..."
                        onSearch={handleSearch}
                        emptyMessage="No audit logs found."
                        showSearch={true}
                        showFilter={true}
                        filterPanel={filterPanel}
                        onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                        isFilterOpen={isFilterOpen}
                    />

                    {/* Pagination */}
                    {!loading && totalRecords > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default AuditLogs;